/** Pure helpers over MatchCard. No scenario logic: everything comes from the server card. */
import type { MatchAction, MatchCard, PartyView } from "@hub/shared";
import type { Tone } from "@/ui";

export const SECTION_ORDER: MatchCard["section"][] = ["waiting_on_you", "in_motion", "completed", "new"];

export const SECTION_LABELS: Record<MatchCard["section"], string> = {
  waiting_on_you: "Waiting on you",
  in_motion: "In motion",
  completed: "Completed",
  new: "New",
};

export const SECTION_HINTS: Record<MatchCard["section"], string> = {
  waiting_on_you: "Introductions you can accept, share or answer.",
  in_motion: "Shared, invited or pending on the other side.",
  completed: "Done. Thank the people who helped.",
  new: "Fresh matches to look at.",
};

export function groupCards(cards: MatchCard[]): { section: MatchCard["section"]; cards: MatchCard[] }[] {
  const sorted = [...cards].sort((a, b) => b.score - a.score || b.updated.localeCompare(a.updated));
  return SECTION_ORDER.map((section) => ({ section, cards: sorted.filter((c) => c.section === section) })).filter((g) => g.cards.length > 0);
}

export function badgeTone(color: MatchCard["display"]["badgeColor"]): Tone {
  return color === "green" ? "success" : color === "blue" ? "info" : "warning";
}

export function scoreTone(label: MatchCard["scoreLabel"]): Tone {
  return label === "high" ? "success" : label === "medium" ? "warning" : "neutral";
}

export function scoreText(label: MatchCard["scoreLabel"], score: number): string {
  const word = label === "high" ? "Strong" : label === "medium" ? "Good" : "Possible";
  return `${word} match, ${Math.round(score * 100)}%`;
}

/** Hidden parties render as "{Owner}'s contact" with no id and no email. */
export function partyLabel(party: PartyView): string {
  if (party.hidden) return `${possessive(party.ownerName)} contact`;
  return party.name;
}

export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

export function primaryAction(card: MatchCard): MatchAction | null {
  return card.actions.find((a) => a.isPrimary && !a.disabled) ?? card.actions.find((a) => !a.disabled && a.id !== "dismiss") ?? null;
}

export function secondaryActions(card: MatchCard, primary: MatchAction | null): MatchAction[] {
  return card.actions.filter((a) => a !== primary);
}

export function costsCredits(action: MatchAction): boolean {
  return action.creditCost > 0;
}

export function cardInvolvesContact(card: MatchCard, contactId: string): boolean {
  return card.needer.contactId === contactId || card.helper.contactId === contactId || card.avatar.primary.contactId === contactId || card.avatar.secondary?.contactId === contactId;
}

export function waitingCount(cards: MatchCard[]): number {
  return cards.filter((c) => c.section === "waiting_on_you").length;
}
